import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getGeminiClient, GEMINI_MODEL } from "../../lib/gemini";
import { getOpenAIClient, OPENAI_MODEL } from "../../lib/openai";
import { SOLUTIONS } from "../commons/SolutionsBar";

import logoGPT from "../../assets/ai/gpt.png";
import logoClaude from "../../assets/ai/claude.png";
import logoMistral from "../../assets/ai/mistral.png";
import logoGemini from "../../assets/ai/gemini.png";

const AI_MODELS = [
  { id: "gpt", label: "GPT-4.1", tone: "clair et structuré" },
  { id: "claude", label: "Claude", tone: "pédagogique et détaillé" },
  { id: "mistral", label: "Mistral", tone: "rapide et synthétique" },
  { id: "gemini", label: "Gemini", tone: "polyvalent et orienté analyse" },
];

const AI_LOGOS = {
  gpt: logoGPT,
  claude: logoClaude,
  mistral: logoMistral,
  gemini: logoGemini,
};

function makeStarterMessages(aiModel) {
  return [
    {
      id: `m-${Date.now()}`,
      role: "assistant",
      content:
        "## Bienvenue dans CirculumAI 👋\n\nPosez une question pour voir comment un chatbot interne pourrait répondre.\n\n- Procédures internes\n- Support équipe\n- RH / onboarding\n- Documentation métier",
      aiModel,
      isTyping: false,
    },
  ];
}

function makeConversation(aiModel) {
  return {
    id: `c-${Date.now()}`,
    title: "Nouvelle conversation",
    updatedAt: Date.now(),
    messages: makeStarterMessages(aiModel),
  };
}

export default function ChatbotSolution({
  selectedSolution,
  historyOpen,
  setHistoryOpen,
}) {
  const [selectedAI, setSelectedAI] = useState("gpt");
  const [input, setInput] = useState("");

  const [conversations, setConversations] = useState(() => {
    const first = {
      id: "c-1",
      title: "Démo — Onboarding",
      updatedAt: Date.now(),
      messages: makeStarterMessages("gpt"),
    };
    return [first];
  });

  const [activeConvId, setActiveConvId] = useState("c-1");

  const activeConv = useMemo(
    () => conversations.find((c) => c.id === activeConvId) || conversations[0],
    [conversations, activeConvId]
  );

  const messages = activeConv?.messages || [];
  const isChatbot = selectedSolution === "chatbot";

  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const scrollToBottom = (behavior = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior, block: "end" });
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const threshold = 140;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceToBottom < threshold;

    shouldAutoScrollRef.current = nearBottom;
    setShowJump(!nearBottom);
  };

  useEffect(() => {
    if (!isChatbot) return;
    if (shouldAutoScrollRef.current) {
      requestAnimationFrame(() => scrollToBottom("smooth"));
    }
  }, [activeConvId, messages.length, isChatbot]);

  useEffect(() => {
    if (!isChatbot) return;
    shouldAutoScrollRef.current = true;
    setShowJump(false);
    requestAnimationFrame(() => scrollToBottom("auto"));
  }, [activeConvId, isChatbot]);

  const updateActiveConversation = (updater) => {
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== activeConvId) return c;
        const next = updater(c);
        return { ...next, updatedAt: Date.now() };
      })
    );
  };

  const renameConversationFromFirstUserMessage = (conv, userText) => {
    if (conv.title && conv.title !== "Nouvelle conversation") return conv;
    const short = userText.replace(/\s+/g, " ").trim().slice(0, 34);
    const title = short.length ? short + (userText.length > 34 ? "…" : "") : "Conversation";
    return { ...conv, title };
  };

  const startNewConversation = () => {
    const nc = makeConversation(selectedAI);
    setConversations((prev) => [nc, ...prev]);
    setActiveConvId(nc.id);
  };

  const handleAIChange = (nextAI) => {
    setSelectedAI(nextAI);
    const aiLabel = getAIModelLabel(nextAI);

    updateActiveConversation((conv) => ({
      ...conv,
      messages: [
        ...conv.messages,
        {
          id: `sys-${Date.now()}`,
          role: "assistant",
          content: `**Modèle actif changé :** ${aiLabel}\n\nVous pouvez continuer la conversation avec ce type d’IA.`,
          aiModel: nextAI,
          isTyping: false,
        },
      ],
    }));
  };

  const handleComposerKeyDown = (e) => {
    if (!isChatbot) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!input.trim()) return;
      handleSend(e);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!isChatbot) return;

    const trimmed = input.trim();
    if (!trimmed) return;

    const userMsg = {
      id: `u-${Date.now()}`,
      role: "user",
      content: trimmed,
      isTyping: false,
    };

    const pendingId = `a-${Date.now() + 1}`;
    const pendingMsg = {
      id: pendingId,
      role: "assistant",
      content: "⏳ Génération en cours…",
      aiModel: selectedAI,
      isTyping: true,
    };

    setInput("");

    shouldAutoScrollRef.current = true;
    setShowJump(false);

    updateActiveConversation((conv) => {
      const renamed = renameConversationFromFirstUserMessage(conv, trimmed);
      return {
        ...renamed,
        messages: [...renamed.messages, userMsg, pendingMsg],
      };
    });

    try {
      const transcript = buildTranscript([...messages, userMsg]);

      const prompt = `
Tu es CirculumAI en mode démo. Réponds en français, ton professionnel, structuré.
Le rendu doit être agréable à lire dans un chat :
- utilise des titres courts si utile
- utilise des listes à puces quand pertinent
- mets en valeur les éléments importants avec **gras**
- reste concis mais utile
Contexte: chatbot interne B2B. Pas de données sensibles.

Conversation:
${transcript}

Nouvelle question:
${trimmed}
`.trim();

      if (selectedAI === "gpt") {
        const client = getOpenAIClient();

        if (!client) {
          updateActiveConversation((conv) => ({
            ...conv,
            messages: conv.messages.map((m) =>
              m.id === pendingId
                ? {
                    ...m,
                    content:
                      "❌ **Clé OpenAI manquante**\n\nAjoute `REACT_APP_OPENAI_API_KEY` dans `.env` puis redémarre `npm start`.",
                    aiModel: "gpt",
                    isTyping: true,
                  }
                : m
            ),
          }));
          return;
        }

        const res = await client.responses.create({
          model: OPENAI_MODEL,
          input: prompt,
        });

        const text = res?.output_text?.trim() || "Réponse vide (OpenAI).";

        updateActiveConversation((conv) => ({
          ...conv,
          messages: conv.messages.map((m) =>
            m.id === pendingId
              ? { ...m, content: text, aiModel: "gpt", isTyping: true }
              : m
          ),
        }));
        return;
      }

      if (selectedAI === "gemini") {
        const client = getGeminiClient();

        if (!client) {
          updateActiveConversation((conv) => ({
            ...conv,
            messages: conv.messages.map((m) =>
              m.id === pendingId
                ? {
                    ...m,
                    content:
                      "❌ **Clé Gemini manquante**\n\nAjoute `REACT_APP_GEMINI_API_KEY` dans `.env` puis redémarre `npm start`.",
                    aiModel: "gemini",
                    isTyping: true,
                  }
                : m
            ),
          }));
          return;
        }

        const res = await client.models.generateContent({
          model: GEMINI_MODEL,
          contents: [{ role: "user", parts: [{ text: prompt }] }],
        });

        const text =
          res?.candidates?.[0]?.content?.parts
            ?.map((p) => p?.text || "")
            .join("")
            .trim() || "Réponse vide (Gemini).";

        updateActiveConversation((conv) => ({
          ...conv,
          messages: conv.messages.map((m) =>
            m.id === pendingId
              ? { ...m, content: text, aiModel: "gemini", isTyping: true }
              : m
          ),
        }));
        return;
      }

      const local = buildDemoAnswer(trimmed, selectedAI);
      updateActiveConversation((conv) => ({
        ...conv,
        messages: conv.messages.map((m) =>
          m.id === pendingId ? { ...m, content: local, isTyping: true } : m
        ),
      }));
    } catch (err) {
      const provider = selectedAI === "gpt" ? "OpenAI" : selectedAI === "gemini" ? "Gemini" : "IA";
      const msg = `❌ **Erreur ${provider}**\n\n${err?.message || String(err)}`;

      updateActiveConversation((conv) => ({
        ...conv,
        messages: conv.messages.map((m) =>
          m.id === pendingId ? { ...m, content: msg, isTyping: true } : m
        ),
      }));
    }
  };

  return (
    <>
      <main className="demo-main">
        {!isChatbot && (
          <div className="demo-nonchat-banner">
            <h2>{getSolutionLabel(selectedSolution)}</h2>
            <p>
              Cette solution sera bientôt disponible en mode démo interactif. Pour l’instant,
              vous pouvez tester le <strong>Chatbot IA</strong>.
            </p>
          </div>
        )}

        <section className={"demo-chat-area" + (!isChatbot ? " demo-chat-area--disabled" : "")}>
          {isChatbot && showJump && (
            <button
              type="button"
              className="demo-jump-bottom"
              onClick={() => {
                shouldAutoScrollRef.current = true;
                setShowJump(false);
                scrollToBottom("smooth");
              }}
            >
              ↓ Revenir en bas
            </button>
          )}

          <div className="demo-chat-scroll" ref={scrollRef} onScroll={handleScroll}>
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                id={m.id}
                role={m.role}
                content={m.content}
                aiModel={m.aiModel}
                isTyping={m.isTyping}
                onTypingDone={(id) => {
                  updateActiveConversation((conv) => ({
                    ...conv,
                    messages: conv.messages.map((msg) =>
                      msg.id === id ? { ...msg, isTyping: false } : msg
                    ),
                  }));
                }}
              />
            ))}
            <div ref={bottomRef} />
          </div>

          <form className="demo-composer" onSubmit={handleSend}>
            <div className={"demo-composer-bar" + (!isChatbot ? " is-disabled" : "")}>
              <textarea
                className="demo-input demo-input--bar"
                placeholder="Décrivez un cas d’usage… (Entrée pour envoyer, Shift+Entrée pour aller à la ligne)"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleComposerKeyDown}
                disabled={!isChatbot}
              />

              <button
                type="submit"
                className="demo-send-btn demo-send-btn--circle"
                disabled={!isChatbot || !input.trim()}
                aria-label="Envoyer"
                title="Envoyer"
              >
                <span aria-hidden="true" className="demo-send-arrow">
                  ➤
                </span>
              </button>

              <div className={"demo-ai-picker" + (!isChatbot ? " is-disabled" : "")}>
                <button
                  type="button"
                  className="demo-ai-trigger"
                  disabled={!isChatbot}
                  aria-label="Choisir un modèle IA"
                  title={`Modèle : ${getAIModelLabel(selectedAI)}`}
                >
                  <img
                    className="demo-ai-logo-img"
                    src={AI_LOGOS[selectedAI]}
                    alt=""
                    aria-hidden="true"
                  />
                </button>

                <div className="demo-ai-menu" role="menu" aria-label="Modèles IA">
                  {AI_MODELS.map((m) => {
                    const active = m.id === selectedAI;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        className={"demo-ai-option" + (active ? " is-active" : "")}
                        onClick={() => handleAIChange(m.id)}
                        role="menuitem"
                        title={`${m.label} — ${m.tone}`}
                        disabled={!isChatbot}
                      >
                        <img
                          className="demo-ai-logo-img"
                          src={AI_LOGOS[m.id]}
                          alt=""
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </div>

                <select
                  className="demo-ai-select demo-ai-select--sr"
                  value={selectedAI}
                  onChange={(e) => handleAIChange(e.target.value)}
                  disabled={!isChatbot}
                  aria-label="Choisir un modèle IA"
                >
                  {AI_MODELS.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="demo-composer-hint">
              Démo · aucun message n’est envoyé à un serveur
            </div>
          </form>
        </section>
      </main>

      <aside className={"demo-rightbar" + (!isChatbot ? " is-disabled" : "")} aria-label="Conversations">
        <div className="demo-rightbar-head">
          <div className="demo-rightbar-title">
            <span className="demo-rightbar-title-main">Conversations</span>
            <span className="demo-rightbar-title-sub">Historique & sessions</span>
          </div>
          <button
            type="button"
            className="demo-rightbar-new"
            onClick={startNewConversation}
            disabled={!isChatbot}
          >
            + Nouveau
          </button>
        </div>

        <div className="demo-rightbar-section">
          <div className="demo-rightbar-label">Démo locale</div>

          <div className="demo-conv-list" role="list">
            {conversations.map((c) => {
              const last = c.messages?.[c.messages.length - 1]?.content || "";
              return (
                <button
                  key={c.id}
                  type="button"
                  className={"demo-conv-item" + (c.id === activeConvId ? " is-active" : "")}
                  onClick={() => setActiveConvId(c.id)}
                  disabled={!isChatbot}
                  role="listitem"
                >
                  <div className="demo-conv-top">
                    <span className="demo-conv-title">{c.title || "Conversation"}</span>
                    <span className="demo-conv-pill">
                      {c.id === activeConvId ? "Active" : "Démo"}
                    </span>
                  </div>
                  <div className="demo-conv-snippet">
                    {last.replace(/\n/g, " ").slice(0, 80)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="demo-rightbar-divider" />

        <div className="demo-rightbar-section">
          <div className="demo-rightbar-label">Historique (connexion)</div>

          <div className="demo-locked-box">
            <div className="demo-locked-title">Connectez-vous</div>
            <div className="demo-locked-text">
              Connectez-vous pour retrouver vos conversations sur tous vos appareils.
            </div>

            <div className="demo-locked-skeleton">
              <div className="demo-locked-row" />
              <div className="demo-locked-row" />
            </div>

            <Link to="/login" className="demo-locked-cta" aria-label="Se connecter pour l'historique">
              Se connecter
            </Link>
          </div>
        </div>

        <div className="demo-rightbar-foot">
          <span>CirculumAI · design B2B</span>
        </div>
      </aside>

      {historyOpen && (
        <div className="demo-drawer" role="dialog" aria-modal="true" aria-label="Historique">
          <button className="demo-drawer-backdrop" aria-label="Fermer" onClick={() => setHistoryOpen(false)} />
          <div className="demo-drawer-panel">
            <div className="demo-drawer-head">
              <div>
                <div className="demo-drawer-title">Conversations</div>
                <div className="demo-drawer-sub">Démo locale & historique</div>
              </div>
              <button className="demo-drawer-close" onClick={() => setHistoryOpen(false)} aria-label="Fermer">
                ✕
              </button>
            </div>

            <button
              type="button"
              className="demo-drawer-new"
              onClick={() => {
                startNewConversation();
                setHistoryOpen(false);
              }}
            >
              + Nouvelle conversation
            </button>

            <div className="demo-rightbar-section demo-rightbar-section--grow">
              <div className="demo-rightbar-label">Démo locale</div>
              <div className="demo-conv-list" role="list">
                {conversations.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={"demo-conv-item" + (c.id === activeConvId ? " is-active" : "")}
                    onClick={() => {
                      setActiveConvId(c.id);
                      setHistoryOpen(false);
                    }}
                    role="listitem"
                  >
                    <div className="demo-conv-top">
                      <span className="demo-conv-title">{c.title || "Conversation"}</span>
                      <span className="demo-conv-pill">
                        {c.id === activeConvId ? "Active" : "Démo"}
                      </span>
                    </div>
                    <div className="demo-conv-snippet">
                      {(c.messages?.[c.messages.length - 1]?.content || "")
                        .replace(/\n/g, " ")
                        .slice(0, 80)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="demo-rightbar-divider" />

            <div className="demo-rightbar-section">
              <div className="demo-rightbar-label">Historique (connexion)</div>
              <div className="demo-locked-box">
                <div className="demo-locked-title">Disponible après connexion</div>
                <div className="demo-locked-text">Historique cloud + synchronisation multi-device.</div>
                <Link to="/login" className="demo-locked-cta">
                  Se connecter
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function getSolutionLabel(id) {
  const found = SOLUTIONS.find((s) => s.id === id);
  return found ? found.label : "";
}

function getAIModelLabel(id) {
  const found = AI_MODELS.find((m) => m.id === id);
  return found ? found.label : "";
}

function buildDemoAnswer(userInput, aiId) {
  const baseSafety =
    "\n\n**Note**\n\nCeci est une démo locale (aucune donnée n’est envoyée à un serveur). En environnement réel, CirculumAI interrogerait un ou plusieurs LLM avec vos règles métier.";

  if (aiId === "claude") {
    return (
      `## Réponse de démonstration (style Claude)\n\n` +
      `**Votre demande** : "${userInput}"\n\n` +
      `Voici une réponse plus pédagogique :\n\n` +
      `- reformulation du besoin\n` +
      `- étapes claires\n` +
      `- précisions utiles pour l’utilisateur\n` +
      baseSafety
    );
  }

  if (aiId === "mistral") {
    return (
      `## Réponse démo (style Mistral)\n\n` +
      `**Demande reçue** : "${userInput}"\n\n` +
      `- réponse rapide\n` +
      `- synthèse courte\n` +
      `- actions prioritaires\n` +
      baseSafety
    );
  }

  if (aiId === "gemini") {
    return (
      `## Réponse démo (style Gemini)\n\n` +
      `**Analyse** de votre demande : "${userInput}"\n\n` +
      `Je peux proposer :\n\n` +
      `- une comparaison\n` +
      `- une synthèse\n` +
      `- une structuration de l’information métier\n` +
      baseSafety
    );
  }

  if (aiId === "gpt") {
    return (
      `## Réponse démo (style GPT)\n\n` +
      `J’ai bien compris votre demande : **"${userInput}"**\n\n` +
      `Je peux fournir une réponse :\n\n` +
      `- claire\n` +
      `- structurée\n` +
      `- exploitable en contexte interne\n\n` +
      `### Cas d’usage possibles\n\n` +
      `- procédures\n` +
      `- support\n` +
      `- RH\n` +
      `- documentation\n` +
      baseSafety
    );
  }

  return (
    `## Réponse démo\n\n` +
    `Votre demande : "${userInput}"\n\n` +
    `Une réponse locale de démonstration a été générée.` +
    baseSafety
  );
}

function buildTranscript(messages) {
  return messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => {
      if (m.role === "user") return `User: ${m.content}`;
      const tag = m.aiModel ? ` (${m.aiModel})` : "";
      return `Assistant${tag}: ${m.content}`;
    })
    .join("\n");
}

function MessageBubble({ id, role, content, aiModel, isTyping, onTypingDone }) {
  const isUser = role === "user";
  const initial = isUser ? "Vous" : "IA";
  const isLoading = content === "⏳ Génération en cours…";

  const [displayedContent, setDisplayedContent] = useState(isUser ? content : content);
  const typingTimerRef = useRef(null);

  useEffect(() => {
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    if (isUser || isLoading || !isTyping) {
      setDisplayedContent(content);
      return;
    }

    setDisplayedContent("");

    let index = 0;
    typingTimerRef.current = setInterval(() => {
      index += 1;
      const next = content.slice(0, index);
      setDisplayedContent(next);

      if (index >= content.length) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
        onTypingDone?.(id);
      }
    }, 10);

    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
      }
    };
  }, [id, content, isUser, isLoading, isTyping, onTypingDone]);

  return (
    <div className={"demo-msg-row " + (isUser ? "is-user" : "is-ai")}>
      <div className="demo-avatar">{initial}</div>

      <div className="demo-msg-wrap">
        {!isUser && aiModel && <div className="demo-msg-model-tag">{getAIModelLabel(aiModel)}</div>}

        <div className={"demo-msg" + (isLoading ? " is-loading" : "")}>
          {isLoading ? (
            <div className="demo-typing-inline" aria-label="Génération en cours">
              <span />
              <span />
              <span />
            </div>
          ) : (
            <div className="demo-msg-content">
              {renderRichText(displayedContent)}
              {!isUser && isTyping && displayedContent.length < content.length && (
                <span className="demo-type-caret" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function renderRichText(text) {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (!line) {
      i += 1;
      continue;
    }

    if (/^###\s+/.test(line)) {
      blocks.push(
        <h4 key={`h4-${i}`} className="demo-msg-h3">
          {renderInlineMarkup(line.replace(/^###\s+/, ""))}
        </h4>
      );
      i += 1;
      continue;
    }

    if (/^##\s+/.test(line)) {
      blocks.push(
        <h3 key={`h3-${i}`} className="demo-msg-h2">
          {renderInlineMarkup(line.replace(/^##\s+/, ""))}
        </h3>
      );
      i += 1;
      continue;
    }

    if (/^#\s+/.test(line)) {
      blocks.push(
        <h2 key={`h2-${i}`} className="demo-msg-h1">
          {renderInlineMarkup(line.replace(/^#\s+/, ""))}
        </h2>
      );
      i += 1;
      continue;
    }

    if (/^[-*•]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*•]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*•]\s+/, ""));
        i += 1;
      }

      blocks.push(
        <ul key={`ul-${i}`} className="demo-msg-ul">
          {items.map((item, idx) => (
            <li key={idx}>{renderInlineMarkup(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i += 1;
      }

      blocks.push(
        <ol key={`ol-${i}`} className="demo-msg-ol">
          {items.map((item, idx) => (
            <li key={idx}>{renderInlineMarkup(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    const paragraphLines = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^#{1,3}\s+/.test(lines[i].trim()) &&
      !/^[-*•]\s+/.test(lines[i].trim()) &&
      !/^\d+\.\s+/.test(lines[i].trim())
    ) {
      paragraphLines.push(lines[i].trim());
      i += 1;
    }

    const paragraph = paragraphLines.join(" ");
    blocks.push(
      <p key={`p-${i}`} className="demo-msg-p">
        {renderInlineMarkup(paragraph)}
      </p>
    );
  }

  return blocks;
}

function renderInlineMarkup(text) {
  const parts = text.split(/(\*\*.*?\*\*)/g).filter(Boolean);

  return parts.map((part, idx) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}